import React, { useState, useEffect, useRef } from 'react';
import { 
  XMarkIcon, 
  UserIcon, 
  DocumentTextIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  UsersIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import BulkShareLinksModal from './BulkShareLinksModal';
import api from '../services/api';

interface Lead {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  company?: string;
  leadUid?: string;
  stage?: string;
  source?: string;
}

interface DocumentType {
  id: number;
  name: string;
  description?: string;
  isRequired: boolean;
}

interface BulkRequestResult {
  leadId: number;
  requestId: number;
  requestToken: string;
  leadName: string;
  leadEmail: string;
  status: 'success' | 'error';
  error?: string;
}

interface BulkRequestDocumentsModalProps {
  onClose: () => void;
  onRequestCreated: () => void;
}

const BulkRequestDocumentsModal: React.FC<BulkRequestDocumentsModalProps> = ({ 
  onClose, 
  onRequestCreated
}) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [allLeads, setAllLeads] = useState<Lead[]>([]); // Store all leads for search
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [selectedLeads, setSelectedLeads] = useState<Set<number>>(new Set());
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState<number[]>([]);
  const [note, setNote] = useState('');
  const [sendEmailAfterCreation, setSendEmailAfterCreation] = useState(false);
  const [leadSearchTerm, setLeadSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'selection' | 'progress' | 'results'>('selection');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalLeadsCount, setTotalLeadsCount] = useState(0);
  const pageSize = 20;
  const hasAutoOpenedRef = useRef(false); // Track if we've auto-opened the modal
  
  // Progress tracking
  const [progress, setProgress] = useState<{
    total: number;
    completed: number;
    current: string;
  }>({ total: 0, completed: 0, current: '' });
  
  // Results
  const [results, setResults] = useState<BulkRequestResult[]>([]);
  const [showShareLinksModal, setShowShareLinksModal] = useState(false);

  useEffect(() => {
    fetchLeads();
    fetchDocumentTypes();
  }, []);

  const fetchLeads = async (pageNum: number = 1, isLoadMore: boolean = false) => {
    try {
      if (!isLoadMore) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      const response = await api.get('/leads', {
        params: {
          page: pageNum,
          limit: pageSize
        }
      });
      
      const newLeads = response.data.data || [];
      const pagination = response.data.pagination;
      
      if (isLoadMore) {
        setLeads(prev => [...prev, ...newLeads]);
        setAllLeads(prev => [...prev, ...newLeads]);
      } else {
        setLeads(newLeads);
        setAllLeads(newLeads);
      }
      
      // Check if there are more leads
      setHasMore(pagination?.hasNextPage || newLeads.length === pageSize);
      setTotalLeadsCount(pagination?.totalRecords || newLeads.length);
      
    } catch (err: any) {
      console.error('Error fetching leads:', err);
      setError('Failed to fetch leads');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchLeads(nextPage, true);
  };

  const fetchDocumentTypes = async () => {
    try {
      const response = await api.get('/documents/types');
      setDocumentTypes(response.data.data || []);
    } catch (err: any) {
      console.error('Error fetching document types:', err);
      setError('Failed to fetch document types');
    }
  };

  // Debounced search function
  const searchLeads = async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      // Reset to showing loaded leads
      return;
    }

    try {
      // Search across ALL leads (no pagination limit for search)
      const response = await api.get('/leads', {
        params: {
          search: searchTerm,
          limit: 1000
        }
      });
      
      const searchResults = response.data.data || [];
      
      // Update allLeads to include search results
      const combinedLeads = [...allLeads];
      searchResults.forEach((searchLead: Lead) => {
        if (!combinedLeads.find(l => l.id === searchLead.id)) {
          combinedLeads.push(searchLead);
        }
      });
      
      setAllLeads(combinedLeads);
    } catch (err: any) {
      console.error('Error searching leads:', err);
    }
  };

  // Debounce search
  useEffect(() => {
    if (leadSearchTerm) {
      const timer = setTimeout(() => {
        searchLeads(leadSearchTerm);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [leadSearchTerm]);

  const handleLeadToggle = (leadId: number) => {
    setSelectedLeads(prev => {
      const newSet = new Set(prev);
      if (newSet.has(leadId)) {
        newSet.delete(leadId);
      } else {
        newSet.add(leadId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    // Select all filtered leads (including search results)
    if (selectedLeads.size === filteredLeads.length && filteredLeads.length > 0) {
      // Deselect all
      setSelectedLeads(new Set());
    } else {
      // Select all filtered leads
      setSelectedLeads(new Set(filteredLeads.map(lead => lead.id)));
    }
  };

  const handleDocumentTypeToggle = (documentTypeId: number) => {
    setSelectedDocumentTypes(prev => 
      prev.includes(documentTypeId)
        ? prev.filter(id => id !== documentTypeId)
        : [...prev, documentTypeId]
    );
  };

  const handleSelectAllDocTypes = () => {
    if (selectedDocumentTypes.length === documentTypes.length) {
      setSelectedDocumentTypes([]);
    } else {
      setSelectedDocumentTypes(documentTypes.map(dt => dt.id));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (selectedLeads.size === 0) {
      setError('Please select at least one lead');
      return;
    }

    if (selectedDocumentTypes.length === 0) {
      setError('Please select at least one document type');
      return;
    }

    // Confirm with user
    const confirmed = window.confirm(
      `You are about to request ${selectedDocumentTypes.length} document type(s) from ${selectedLeads.size} lead(s). Do you want to continue?`
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      setError(null);
      setStep('progress');
      
      const leadIds = Array.from(selectedLeads);
      setProgress({
        total: leadIds.length,
        completed: 0,
        current: 'Starting...'
      });

      const response = await api.post('/documents/requests/bulk', {
        leadIds,
        documentTypeIds: selectedDocumentTypes,
        note: note.trim() || undefined
      });

      if (response.data.success || response.data.data) {
        const bulkResults = response.data.data;
        setResults(bulkResults.requests || []);
        setProgress({
          total: bulkResults.totalLeads,
          completed: bulkResults.totalLeads,
          current: 'Completed'
        });
        setStep('results');
      } else {
        throw new Error(response.data.message || 'Failed to create bulk document requests');
      }
    } catch (err: any) {
      console.error('Error creating bulk document requests:', err);
      setError(err.response?.data?.message || 'Failed to create bulk document requests');
      setStep('selection');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    setShowShareLinksModal(false);
    onRequestCreated();
    onClose();
  };

  const filteredLeads = allLeads.filter(lead => {
    if (!leadSearchTerm) return leads.some(l => l.id === lead.id); // Show only loaded leads when no search
    
    const searchLower = leadSearchTerm.toLowerCase();
    const fullName = `${lead.firstName} ${lead.lastName}`.toLowerCase();
    const email = lead.email.toLowerCase();
    const company = lead.company?.toLowerCase() || '';
    const leadUid = lead.leadUid?.toLowerCase() || '';
    
    return fullName.includes(searchLower) || 
           email.includes(searchLower) || 
           company.includes(searchLower) ||
           leadUid.includes(searchLower);
  });

  const selectedDocumentTypesData = documentTypes.filter(dt => 
    selectedDocumentTypes.includes(dt.id)
  );

  const selectedLeadsData = leads.filter(lead => selectedLeads.has(lead.id));

  const successCount = results.filter(r => r.status === 'success').length;
  const errorCount = results.filter(r => r.status === 'error').length;

  // Auto-open share links modal if email option is selected (only once)
  useEffect(() => {
    if (step === 'results' && sendEmailAfterCreation && successCount > 0 && !showShareLinksModal && !hasAutoOpenedRef.current) {
      // Mark that we've auto-opened
      hasAutoOpenedRef.current = true;
      
      // Small delay to let the results page render first
      const timer = setTimeout(() => {
        setShowShareLinksModal(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [step, sendEmailAfterCreation, successCount, showShareLinksModal]);

  // Selection Step
  if (step === 'selection') {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-10 mx-auto p-6 border w-11/12 max-w-4xl shadow-lg rounded-lg bg-white mb-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <UsersIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Bulk Document Request</h3>
                <p className="text-sm text-gray-500">Request documents from multiple leads at once</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
              <ExclamationCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Lead Selection */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Select Leads *
                </label>
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-600">
                    {selectedLeads.size} selected
                    {totalLeadsCount > 0 && ` of ${totalLeadsCount} total`}
                  </span>
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {selectedLeads.size === filteredLeads.length && filteredLeads.length > 0
                      ? 'Clear All'
                      : leadSearchTerm 
                        ? `Select All Search Results (${filteredLeads.length})`
                        : 'Select All'}
                  </button>
                </div>
              </div>

              {/* Search */}
              <div className="relative mb-3">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search leads by UID, name, email, or company..."
                  value={leadSearchTerm}
                  onChange={(e) => setLeadSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Lead List */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                  {filteredLeads.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500">
                      {leadSearchTerm ? 'No leads found matching your search' : 'No leads available'}
                    </div>
                  ) : (
                    <>
                      {filteredLeads.map((lead) => (
                        <label
                          key={lead.id}
                          className="flex items-center space-x-3 p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedLeads.has(lead.id)}
                            onChange={() => handleLeadToggle(lead.id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <UserIcon className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {lead.firstName} {lead.lastName}
                              </div>
                              {lead.leadUid && (
                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded flex-shrink-0">
                                  {lead.leadUid}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500 truncate">{lead.email}</div>
                            {lead.company && (
                              <div className="text-xs text-gray-400 truncate">{lead.company}</div>
                            )}
                          </div>
                        </label>
                      ))}
                      
                      {/* Load More Button */}
                      {!leadSearchTerm && hasMore && (
                        <div className="p-3 border-t border-gray-200 bg-gray-50">
                          <button
                            type="button"
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                            className="w-full px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {loadingMore ? (
                              <div className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                                Loading more leads...
                              </div>
                            ) : (
                              `Load More Leads (${leads.length} of ${totalLeadsCount} loaded)`
                            )}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Info Message */}
              {leadSearchTerm && (
                <div className="mt-2 text-xs text-gray-600 bg-yellow-50 border border-yellow-200 rounded px-3 py-2">
                  💡 <strong>Search Tip:</strong> Searching across all {totalLeadsCount} leads. "Select All" will select all search results.
                </div>
              )}

              {/* Selected Leads Summary */}
              {selectedLeads.size > 0 && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-900">
                      {selectedLeads.size} lead{selectedLeads.size !== 1 ? 's' : ''} selected
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedLeads(new Set())}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Clear Selection
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Document Types Selection */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Select Document Types *
                </label>
                <button
                  type="button"
                  onClick={handleSelectAllDocTypes}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  {selectedDocumentTypes.length === documentTypes.length && documentTypes.length > 0
                    ? 'Clear All'
                    : 'Select All'}
                </button>
              </div>

              <div className="border border-gray-200 rounded-lg p-4 space-y-3 max-h-48 overflow-y-auto">
                {documentTypes.length === 0 ? (
                  <div className="text-sm text-gray-500 text-center">No document types available</div>
                ) : (
                  documentTypes.map((docType) => (
                    <label key={docType.id} className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedDocumentTypes.includes(docType.id)}
                        onChange={() => handleDocumentTypeToggle(docType.id)}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">
                            {docType.name}
                          </span>
                          {docType.isRequired && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Required
                            </span>
                          )}
                        </div>
                        {docType.description && (
                          <div className="text-sm text-gray-500 mt-0.5">{docType.description}</div>
                        )}
                      </div>
                    </label>
                  ))
                )}
              </div>

              {selectedDocumentTypesData.length > 0 && (
                <div className="mt-3">
                  <div className="text-sm font-medium text-gray-700 mb-2">Selected Document Types:</div>
                  <div className="flex flex-wrap gap-2">
                    {selectedDocumentTypesData.map((docType) => (
                      <span
                        key={docType.id}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        <DocumentTextIcon className="w-3 h-3 mr-1.5" />
                        {docType.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Note */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note (Optional)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Add instructions that will be included in all document requests..."
              />
              <p className="mt-1 text-xs text-gray-500">
                This note will be visible to all selected leads when they receive the document request.
              </p>
            </div>

            {/* Send Email Option */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendEmailAfterCreation}
                  onChange={(e) => setSendEmailAfterCreation(e.target.checked)}
                  className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-semibold text-gray-900">
                      📧 Send email notifications to all leads
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    Automatically send document request emails to all leads after creating the requests. You can customize the email content before sending.
                  </p>
                </div>
              </label>
            </div>

            {/* Summary Box */}
            {selectedLeads.size > 0 && selectedDocumentTypes.length > 0 && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Request Summary</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Total Leads:</span>
                    <span className="ml-2 font-semibold text-gray-900">{selectedLeads.size}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Document Types:</span>
                    <span className="ml-2 font-semibold text-gray-900">{selectedDocumentTypes.length}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-600">Total Requests to Create:</span>
                    <span className="ml-2 font-semibold text-blue-600 text-lg">{selectedLeads.size}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || selectedLeads.size === 0 || selectedDocumentTypes.length === 0}
                className="px-5 py-2.5 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </div>
                ) : (
                  `Create ${selectedLeads.size} Request${selectedLeads.size !== 1 ? 's' : ''}`
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Progress Step
  if (step === 'progress') {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-1/4 mx-auto p-6 border w-11/12 max-w-md shadow-lg rounded-lg bg-white">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Creating Document Requests</h3>
            <p className="text-sm text-gray-600 mb-4">{progress.current}</p>
            
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${(progress.completed / progress.total) * 100}%` }}
              ></div>
            </div>
            
            <p className="text-sm font-medium text-gray-700">
              {progress.completed} of {progress.total} completed
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Results Step
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-6 border w-11/12 max-w-3xl shadow-lg rounded-lg bg-white mb-10">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircleIcon className="w-7 h-7 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Bulk Request Complete</h3>
                <p className="text-sm text-gray-600">
                  {successCount} of {results.length} request{results.length !== 1 ? 's' : ''} created successfully
                </p>
              </div>
            </div>
            <button
              onClick={handleComplete}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-600">{results.length}</div>
              <div className="text-sm text-blue-700">Total Processed</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-600">{successCount}</div>
              <div className="text-sm text-green-700">Successful</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-red-600">{errorCount}</div>
              <div className="text-sm text-red-700">Failed</div>
            </div>
          </div>
        </div>

        {/* Results List */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Request Details</h4>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
              {results.map((result) => (
                <div
                  key={result.leadId}
                  className={`p-4 flex items-center justify-between ${
                    result.status === 'success' ? 'bg-white' : 'bg-red-50'
                  }`}
                >
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    {result.status === 'success' ? (
                      <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <ExclamationCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {result.leadName}
                      </div>
                      <div className="text-sm text-gray-500 truncate">{result.leadEmail}</div>
                      {result.error && (
                        <div className="text-xs text-red-600 mt-1">{result.error}</div>
                      )}
                    </div>
                  </div>
                  {result.status === 'success' && (
                    <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full flex-shrink-0">
                      Request #{result.requestId}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
          <div className="flex space-x-3">
            <button
              onClick={() => setShowShareLinksModal(true)}
              disabled={successCount === 0}
              className="px-4 py-2 border border-blue-600 rounded-lg text-sm font-medium text-blue-600 bg-white hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {sendEmailAfterCreation ? 'Send Emails Now' : 'View All Share Links'}
            </button>
          </div>
          <button
            onClick={handleComplete}
            className="px-5 py-2.5 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            {sendEmailAfterCreation ? 'Skip & Close' : 'Done'}
          </button>
        </div>
      </div>

      {/* Bulk Share Links Modal */}
      {showShareLinksModal && (
        <BulkShareLinksModal
          results={results.filter(r => r.status === 'success')}
          documentTypes={selectedDocumentTypesData.map(dt => dt.name)}
          onClose={() => {
            setShowShareLinksModal(false);
            // If email was sent and user closes, also close parent modal
            if (sendEmailAfterCreation) {
              handleComplete();
            }
          }}
          autoOpenEmailModal={sendEmailAfterCreation}
        />
      )}
    </div>
  );
};

export default BulkRequestDocumentsModal;
